import { MouseEvent, useCallback, useContext, useMemo, useState } from "react";
import { NavigateOptions, useNavigate } from "react-router-dom";
import { HomeContext } from "../../containers/Home";
import { DashboardTenant } from "../../utils/dashboard/moduleTenants";
import { AllTenantsMode, tenantChoice } from "./tenantChoice";

/** A widget's tenant select: its options, the selected value, and what it resolves to. */
export const useTenantChoice = (
	tenants: DashboardTenant[],
	mode: AllTenantsMode,
	currentTenantKey: string
) => {
	const choice = useMemo(
		() => tenantChoice(tenants, mode, currentTenantKey),
		[tenants, mode, currentTenantKey]
	);
	const [tenant, setTenant] = useState(choice.defaultValue);
	return { choice, tenant, setTenant, ...choice.resolve(tenant) };
};

/**
 * Open state of a widget's expanded view. `session` changes on every open;
 * used as the view's `key`, it starts each visit from the widget's selection.
 */
export const useExpandedView = () => {
	const [state, setState] = useState({ open: false, session: 0 });
	return {
		...state,
		show: () =>
			setState(({ session }) => ({ open: true, session: session + 1 })),
		hide: () => setState(({ session }) => ({ open: false, session })),
	};
};

/**
 * Whether an expanded view has been open since it mounted. It stays true
 * while the view closes, so its query keeps its data instead of flashing a
 * skeleton, and a view that was never opened fetches nothing.
 */
export const useHasOpened = (open: boolean) => {
	const [opened, setOpened] = useState(open);
	if (open && !opened) setOpened(true);
	return opened;
};

/** Filter-bar values: edits stay in `draft` until applied; reset restores `initial`. */
export const useDraftFilters = <T extends object>(initial: T) => {
	const [draft, setDraft] = useState(initial);
	const [applied, setApplied] = useState(initial);
	return {
		draft,
		applied,
		update: (patch: Partial<T>) =>
			setDraft((current) => ({ ...current, ...patch })),
		apply: () => setApplied(draft),
		reset: () => {
			setDraft(initial);
			setApplied(initial);
		},
	};
};

/** Navigation behind Home's unsaved-changes guard, as the sidebar does. */
export const useGuardedNavigate = () => {
	const { handlePageChange }: any = useContext(HomeContext);
	const navigate = useNavigate();

	/** `beforeNavigate` runs only once the user has agreed to leave. */
	const go = useCallback(
		(to: string, options?: NavigateOptions, beforeNavigate?: () => void) => {
			// The guard rejects when the user chooses to stay on the page.
			Promise.resolve(handlePageChange?.()).then(
				() => {
					beforeNavigate?.();
					navigate(to, options);
				},
				() => undefined
			);
		},
		[handlePageChange, navigate]
	);

	/** For `<a href>`: plain clicks go through the guard, modified clicks stay native. */
	const onLinkClick =
		(to: string) => (event: MouseEvent<HTMLAnchorElement>) => {
			if (
				event.button !== 0 ||
				event.metaKey ||
				event.ctrlKey ||
				event.shiftKey ||
				event.altKey
			) {
				return;
			}
			event.preventDefault();
			go(to);
		};

	return { go, onLinkClick };
};
